import { lazy, ReactElement, Suspense } from 'react'

import queryString from 'query-string'
import { Navigate, Outlet } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { DatadogService } from 'const/monitoring/datadog'
import { RumViewTracker } from 'features/Monitoring/Datadog/RumViewTracker'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { RemoteModule } from 'remoteModules/RemoteModule'
import { AppLayoutContainer } from 'views/AppLayout'
import AuthLayout from 'views/Auth/AuthLayout'
import AuthOrganizationPage from 'views/Auth/AuthOrganizationPage'
import {
    CreateAccountForm,
    ForgotPasswordForm,
    LoginForm,
    ResetPasswordForm,
    SmsCodeForm,
    VerifyEmailForm,
} from 'views/Auth/Forms'
import InvitationSignUpPage from 'views/Auth/InvitationSignUpPage'
import SetUpPhoneNumberPage from 'views/AuthConfiguration/SetUpPhoneNumberPage'
import { DashboardPrintPage } from 'views/DashboardPrintPage'
import { ZendeskSSOPage } from 'views/ZendeskSSOPage'

import { getPath } from './pagePaths'
import PrivateRoute from './PrivateRoute'
import RoutingServiceInitializer from './RoutingServiceInitializer'

// Lazy so the executive home page module stays out of the app bundle: this route is only
// ever visited by the headless PDF worker.
const HomePagePrintPage = lazy(
    () =>
        import(
            /* webpackChunkName: "executiveHomePagePrint" */ 'dynamicModules/ExecutiveHomePage/HomePagePrintPage/HomePagePrintPage'
        )
)

function AppWrapper(props: any): ReactElement {
    return (
        <QueryParamProvider
            adapter={ReactRouter6Adapter}
            options={{
                searchStringToObject: queryString.parse,
                objectToSearchString: queryString.stringify,
            }}
        >
            <RoutingServiceInitializer />
            <Outlet {...props} />
        </QueryParamProvider>
    )
}

function AIReportPrintRoute(): ReactElement {
    const featureFlags = useFeatureFlags()

    return (
        <RemoteModule
            scope="rocket"
            module="./AiReportPrint"
            componentProps={{ featureFlags }}
        />
    )
}

// Anyone holding a public link lands here, signed in or not, so it sits outside
// PrivateRoute and takes no feature flags: an anonymous visitor has none.
function AIReportSharedRoute(): ReactElement {
    return (
        <>
            <RumViewTracker
                service={DatadogService.AI}
                name={getPath('aiReportSharedPage')}
            />
            <RemoteModule scope="rocket" module="./AiReportShared" />
        </>
    )
}

export const BASE_ROUTES = [
    {
        element: <AppWrapper />,
        children: [
            {
                path: getPath('authPage'),
                element: <AuthLayout />,
                children: [
                    {
                        path: getPath('authSignUpPage'),
                        element: <CreateAccountForm />,
                    },
                    {
                        path: getPath('authVerifyEmailPage'),
                        element: <VerifyEmailForm />,
                    },
                    {
                        path: getPath('authOrganizationPage'),
                        element: <AuthOrganizationPage />,
                    },
                    {
                        path: getPath('authInvitationSignupPage'),
                        element: <InvitationSignUpPage />,
                    },
                    {
                        path: getPath('authForgotPasswordPage'),
                        element: <ForgotPasswordForm />,
                    },
                    {
                        path: getPath('authResetPasswordPage'),
                        element: <ResetPasswordForm />,
                    },
                    {
                        path: getPath('authSignInMfaPage'),
                        element: <SmsCodeForm />,
                    },
                    {
                        path: getPath('authPage'),
                        element: <LoginForm />,
                    },
                    {
                        path: '*',
                        element: <Navigate to={getPath('authPage')} />,
                    },
                ],
            },
            {
                path: getPath('authSignUpMfaSetupPage'),
                element: <SetUpPhoneNumberPage />,
            },
            {
                path: getPath('aiReportSharedPage'),
                element: <AIReportSharedRoute />,
            },
            {
                path: getPath('dashboardPrintPage'),
                element: <PrivateRoute />,
                children: [
                    {
                        path: '',
                        element: <DashboardPrintPage />,
                    },
                ],
            },
            {
                path: getPath('homePagePrintPage'),
                element: <PrivateRoute />,
                children: [
                    {
                        path: '',
                        element: (
                            <Suspense fallback={null}>
                                <HomePagePrintPage />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: getPath('aiReportPrintPage'),
                element: <PrivateRoute />,
                children: [
                    {
                        path: '',
                        element: <AIReportPrintRoute />,
                    },
                ],
            },
            {
                path: getPath('zendeskSSOPage'),
                element: <PrivateRoute />,
                children: [
                    {
                        path: '',
                        element: <ZendeskSSOPage />,
                    },
                ],
            },
            {
                path: '*',
                element: <PrivateRoute />,
                children: [
                    {
                        path: '*',
                        element: <AppLayoutContainer />,
                    },
                ],
            },
        ],
    },
]
